import { ReportDefinition } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';

type OutOfOrderRoomRow = {
   rowId: string;
   room: string;
   type: string;
   reason: string;
   reasonCode: string;
   description: string;
   from: string;
   fromRaw: string;
   to: string;
   toRaw: string;
   returnStatus: string;
   remarks: string;
   status: string;
   hasRealTyallsRowId: boolean;
};

function asString(value: any): string {
   if (value === null || value === undefined) return '';
   return String(value);
}

function parseReportJson(reportJson: any): any {
   if (typeof reportJson !== 'string') return reportJson;

   try {
      return JSON.parse(reportJson);
   } catch {
      return {};
   }
}

function formatDate(value: any): string {
   const raw = asString(value).trim();
   if (!raw) return '';

   const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
   if (isoMatch) {
      return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
   }

   const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
   if (slashMatch) {
      return `${slashMatch[1].padStart(2, '0')}/${slashMatch[2].padStart(
         2,
         '0'
      )}/${slashMatch[3]}`;
   }

   return raw;
}

function getRealTyallsRowId(row: any): string {
   return asString(row?.tyallsRowId ?? row?.TyallsRowId);
}

/**
 * Row ID rules:
 * - Prefer real TyallsRowId because row annotations depend on it.
 * - Fall back to Id/id.
 * - As a last resort, generate a deterministic frontend-only ID.
 */
function getRowId(row: any): string {
   const realTyallsRowId = getRealTyallsRowId(row);
   if (realTyallsRowId) return realTyallsRowId;

   return (
      asString(row?.Id ?? row?.id) ||
      `out-of-order-room:${asString(row?.RoomNumber)}:${asString(
         row?.ReasonCode
      )}:${asString(row?.From ?? row?.Start)}:${asString(row?.To ?? row?.End)}`
   );
}

function getReasonDisplay(row: any): string {
   const reasonCode = asString(row?.ReasonCode);
   const description = asString(row?.Description ?? row?.ReasonDescription);

   if (reasonCode && description) return `${reasonCode}\n${description}`;
   if (description) return description;
   return reasonCode;
}

function getSourceRows(reportJson: any): any[] {
   const parsed = parseReportJson(reportJson);

   /**
    * New Task Generator shape:
    * {
    *   OutOfOrderRooms: [
    *     {
    *       RoomNumber,
    *       RoomType,
    *       ReasonCode,
    *       Description,
    *       From,
    *       To,
    *       ReturnStatus,
    *       Remarks,
    *       Status,
    *       TyallsRowId
    *     }
    *   ]
    * }
    */
   if (Array.isArray(parsed?.OutOfOrderRooms)) {
      return parsed.OutOfOrderRooms;
   }

   /**
    * Backward compatibility with older nested shape:
    * {
    *   Rooms: [
    *     {
    *       RoomNumber,
    *       RoomType,
    *       OutOfOrderValues: [...]
    *     }
    *   ]
    * }
    */
   if (Array.isArray(parsed?.Rooms)) {
      const rows: any[] = [];

      for (const room of parsed.Rooms) {
         const values = Array.isArray(room?.OutOfOrderValues)
            ? room.OutOfOrderValues
            : [];

         for (const value of values) {
            rows.push({
               ...value,
               RoomNumber: room?.RoomNumber,
               RoomType: room?.RoomType,
               Description: value?.ReasonDescription,
               From: value?.Start,
               To: value?.End,
               ReturnStatus: value?.ReturnStatus ?? value?.HousekeepingStatus,
               Remarks: value?.Remarks ?? value?.Remark,
               Status: value?.Status
            });
         }
      }

      return rows;
   }

   return [];
}

function normaliseRows(reportJson: any): OutOfOrderRoomRow[] {
   return getSourceRows(reportJson).map((row) => {
      const fromRaw = asString(row?.From ?? row?.Start);
      const toRaw = asString(row?.To ?? row?.End);

      return {
         rowId: getRowId(row),
         room: asString(row?.RoomNumber),
         type: asString(row?.RoomType),
         reason: getReasonDisplay(row),
         reasonCode: asString(row?.ReasonCode),
         description: asString(row?.Description ?? row?.ReasonDescription),
         from: formatDate(fromRaw),
         fromRaw,
         to: formatDate(toRaw),
         toRaw,
         returnStatus: asString(row?.ReturnStatus ?? row?.HousekeepingStatus),
         remarks: asString(row?.Remarks ?? row?.Remark),
         status: asString(row?.Status),
         hasRealTyallsRowId: !!getRealTyallsRowId(row)
      };
   });
}

function sortRows(rows: OutOfOrderRoomRow[]): OutOfOrderRoomRow[] {
   return [...rows].sort((a, b) => {
      const aRoom = Number(a.room);
      const bRoom = Number(b.room);

      if (Number.isFinite(aRoom) && Number.isFinite(bRoom)) {
         return aRoom - bRoom;
      }

      return a.room.localeCompare(b.room);
   });
}

export const OUT_OF_ORDER_ROOMS_DEFINITION: ReportDefinition = {
   reportType: 'OutOfOrderByReason',
   version: 3,
   title: 'Out of Order Rooms',
   sorting: {
      enabled: true,
      persist: true
   },
   header: {
      title: 'Out of Order Rooms',
      snippets: ({ allRows }) => {
         const dataRows = allRows.filter(
            (row) => row.cells?.['rowType'] === 'outOfOrderRoom'
         );

         return [
            {
               label: 'Count',
               value: dataRows.length
            }
         ];
      }
   },

   rowChecks: {
      enabled: false,
      includeNestedRows: false,
      selectableRowTypes: ['outOfOrderRoom']
   },

   annotations: {
      enabled: true,
      selectableRowTypes: ['outOfOrderRoom'],
      disabledMessage:
         'Row comments and row highlighting are not available for this report layout. Please use the main report comments panel to add comments for this report.'
   },

   columns: [
      { key: 'room', header: 'ROOM', width: '90px', sort: { type: 'string' } },
      { key: 'type', header: 'TYPE', width: '90px', sort: { type: 'string' } },
      {
         key: 'reason',
         header: 'REASON',
         width: '170px',
         maxChars: 80,
         sort: { type: 'string' }
      },
      {
         key: 'from',
         header: 'FROM',
         width: '120px',
         sort: { type: 'date', getValue: (row) => row.cells['fromRaw'] }
      },
      {
         key: 'to',
         header: 'TO',
         width: '120px',
         sort: { type: 'date', getValue: (row) => row.cells['toRaw'] }
      },
      {
         key: 'returnStatus',
         header: 'RETURN ST.',
         width: '130px',
         sort: { type: 'string' }
      },
      {
         key: 'remarks',
         header: 'REMARKS',
         width: '360px',
         maxChars: 140,
         sort: { type: 'string' }
      },
      { key: 'status', header: 'STATUS', width: '130px', sort: { type: 'string' } }
   ],

   filter: {
      fields: [
         { key: 'room', label: 'Room', type: 'string' },
         { key: 'type', label: 'Type', type: 'string' },
         { key: 'reasonCode', label: 'Reason Code', type: 'string' },
         { key: 'description', label: 'Description', type: 'string' },
         {
            key: 'from',
            label: 'From',
            type: 'date',
            getValue: (row) => row.cells['fromRaw']
         },
         {
            key: 'to',
            label: 'To',
            type: 'date',
            getValue: (row) => row.cells['toRaw']
         },
         { key: 'returnStatus', label: 'Return Status', type: 'string' },
         { key: 'remarks', label: 'Remarks', type: 'string' },
         { key: 'status', label: 'Status', type: 'string' }
      ]
   },

   flattenRows: (reportJson: any): ReportRowVm[] => {
      const rows = sortRows(normaliseRows(reportJson));

      return rows.map((row) => ({
         rowId: row.rowId,
         isAnnotatable: row.hasRealTyallsRowId,
         cells: {
            rowType: 'outOfOrderRoom',
            room: row.room,
            type: row.type,
            reason: row.reason,
            reasonCode: row.reasonCode,
            description: row.description,
            from: row.from,
            fromRaw: row.fromRaw,
            to: row.to,
            toRaw: row.toRaw,
            returnStatus: row.returnStatus,
            remarks: row.remarks,
            status: row.status
         }
      }));
   }
};
